import { css, Theme, Interpolation } from '@emotion/react';
import { ServiceQualityFlag } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { CallIntelTypes, ServiceQualityFlagPriorityMap } from '@frontend/api-call-intel';
import { AnimatedBadge } from '@frontend/components';
import { useTranslation, i18next } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { theme } from '@frontend/theme';
import { TableLoadingSkeleton, Text, TextLink, Truncated } from '@frontend/design-system';
import { useCallIntelContactTypesLabel, useCallIntelDefaultsShallowStore } from '../../hooks/use-call-intel-defaults';
import {
  callStatusUtils,
  getReasonText,
  getReasonTooltipLabel,
  sortWithPriorityMap,
} from '../../utils/call-intel-utils';
import { CallIntelChip } from '../chip/call-intel-chip';
import { SchedulingChip } from '../chip/scheduling-chip';
import TaskChip from '../chip/task-chip';
import { ChipGroup } from '../chip-group';
import { ColumnHeaderInfoTip } from '../column-header-info-tip';
import { EmptyCell } from '../empty-cell';
import { PrivateField } from '../private-field';
import { CallIntelTooltip } from '../tooltip/call-intel-tooltip';

type CallRecordStatus = 'abandoned' | 'missed';

type ColumnCellProps = {
  direction?: string;
  callInsights: Call;
  callRecordStatus?: CallRecordStatus;
  isSmallMode?: boolean;
  isCallIntelAccessRestricted?: boolean;
  callIntelAccessRestrictedTooltip?: string;
  showReason?: boolean;
};

type ColumnHeaderProps = { showCallIntelPrefix?: boolean; showAiIcon?: boolean };

type CommonCellRendererOverrides = {
  outboundNoInsightsTooltip?: string;
};

type ShouldRenderConfig = {
  // Check if content exists (e.g., has sentiment, has tasks)
  checkContent?: (callInsights: Call, props?: Record<string, any>) => boolean;
  // Check if feature is enabled
  checkFeatureEnabled?: (props: ColumnCellProps & Record<string, any>) => boolean;
  // Check if call state allows rendering (default checks are applied)
  skipDefaultChecks?: boolean;
};

type CallIntelCellContentProps = {
  children: React.ReactNode;
  editedText?: string;
  isEdited?: boolean;
  customStyles?: Interpolation<Theme>;
  reason?: string;
  showReason?: boolean;
};

const createShouldRender = (config: ShouldRenderConfig) => {
  return (props: ColumnCellProps & Record<string, unknown>): boolean => {
    const { callInsights, callRecordStatus, direction, isSmallMode } = props;

    // Apply default checks unless skipped
    if (!config.skipDefaultChecks) {
      if (direction === 'outbound' && !callInsights) return false;
      if (callRecordStatus === 'missed' || callRecordStatus === 'abandoned') return false;
      if (!callInsights) return false;

      const { isFailedCall, isProcessingCall, isSkippedCall, isVoicemailCall } =
        callStatusUtils.getCallFlags(callInsights);

      if (isProcessingCall || isFailedCall || isSkippedCall || isVoicemailCall) return false;
    }

    // Check feature enabled (e.g., tasks disabled)
    if (config.checkFeatureEnabled && !config.checkFeatureEnabled(props)) {
      return false;
    }

    // For small mode, check if content exists
    if (isSmallMode && config.checkContent) {
      return config.checkContent(callInsights);
    }

    return true;
  };
};

const getCommonCellRenderer = ({
  callInsights,
  direction,
  callRecordStatus,
  isCallIntelAccessRestricted,
  callIntelAccessRestrictedTooltip,
  overrides,
  checkForPrivate = true,
}: ColumnCellProps & { overrides?: CommonCellRendererOverrides; checkForPrivate?: boolean }) => {
  if (direction === 'outbound' && !callInsights)
    return (
      <EmptyCell tooltipText={overrides?.outboundNoInsightsTooltip ?? i18next.t('Outbound call', { ns: 'calls' })} />
    );

  if (callRecordStatus === 'missed')
    return <EmptyCell tooltipText={i18next.t('Missed call', { ns: 'call-intel-core' })} />;

  if (callRecordStatus === 'abandoned')
    return <EmptyCell tooltipText={i18next.t('Abandoned call', { ns: 'call-intel-core' })} />;

  if (isCallIntelAccessRestricted) {
    return (
      <EmptyCell
        tooltipText={
          callIntelAccessRestrictedTooltip ??
          i18next.t('Call Intelligence is not available for this location.', { ns: 'call-intel-core' })
        }
      />
    );
  }

  if (!callInsights) return <EmptyCell showTooltip={false} />;

  const { isFailedCall, isSkippedCall, isProcessingCall, isPrivateCall, isVoicemailCall, isDeferredCall } =
    callStatusUtils.getCallFlags(callInsights);

  if (isDeferredCall || isProcessingCall) return <TableLoadingSkeleton />;

  if (checkForPrivate && isPrivateCall) {
    return <PrivateField />;
  }

  if (isVoicemailCall) return <EmptyCell tooltipText={i18next.t('Outbound Voicemail', { ns: 'call-intel-core' })} />;

  if (isFailedCall)
    return <EmptyCell tooltipText={i18next.t('Failed Call Intelligence analysis', { ns: 'call-intel-core' })} />;

  if (isSkippedCall)
    return <EmptyCell tooltipText={i18next.t('Skipped Call Intelligence analysis', { ns: 'call-intel-core' })} />;

  return null;
};

const CallIntelCellContent: React.FC<CallIntelCellContentProps> = ({
  children,
  editedText,
  isEdited,
  customStyles,
  reason,
  showReason = false,
}) => {
  const shouldRenderReason = showReason && !!reason;

  return (
    <div css={customStyles || styles.fieldWrapper}>
      {children}
      {isEdited && editedText && (
        <Text size='small' color='subdued' css={styles.editedText}>
          {editedText}
        </Text>
      )}

      {shouldRenderReason && (
        <Truncated as={Text} size='medium' css={styles.reasonText}>
          {reason}
        </Truncated>
      )}
    </div>
  );
};

export const ContactTypeColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Contact Type')}
        infoTip={<CallIntelTooltip noCta tip='contactType' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isSmallMode,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
  }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const contactTypeLabelMap = useCallIntelContactTypesLabel();
    const { chipVariants } = useCallIntelDefaultsShallowStore('chipVariants');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
      checkForPrivate: false,
    });
    if (renderer) return isSmallMode ? null : renderer;

    const { contactType, isFieldEdited } = callInsights;

    if (!contactType) return <EmptyCell showTooltip={false} />;

    if (isSmallMode) {
      return (
        <CallIntelChip
          chipVariants={chipVariants}
          dataKey={contactType}
          label={contactTypeLabelMap?.[contactType] ?? ''}
        />
      );
    }

    return (
      <CallIntelCellContent
        customStyles={styles.chips}
        isEdited={isFieldEdited?.isContactTypeEdited}
        editedText={t('Edited')}
      >
        <CallIntelChip
          chipVariants={chipVariants}
          dataKey={contactType}
          label={contactTypeLabelMap?.[contactType] ?? ''}
        />
      </CallIntelCellContent>
    );
  },
  HeaderLabel: () => i18next.t('Contact Type', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => !!callInsights.contactType,
  }),
};

export const SchedulingOutcomeColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Scheduling Outcome')}
        infoTip={<CallIntelTooltip noCta tip='schedulingOutcomeV2' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isSmallMode,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
    showReason = false,
  }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { nonPatient: nonPatientToken } = useTranslationTokens();
    const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
    });
    if (renderer) return isSmallMode ? null : renderer;

    const { schedulingOutcomeV2, isFieldEdited } = callInsights;

    const reasonText = getReasonText(callInsights.schedulingOutcomeDetails?.reasons);

    const shouldShowReason =
      showReason &&
      schedulingOutcomeV2 === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED &&
      !!reasonText;

    if (schedulingOutcomeV2) {
      if (
        [
          CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NOT_APPLICABLE,
          CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN,
        ].includes(schedulingOutcomeV2)
      ) {
        if (isSmallMode) return null;
        return (
          <EmptyCell
            edited={isFieldEdited?.isSchedulingOutcomeEdited}
            tooltipText={t('Scheduling Outcome isn’t analyzed for {{nonPatientToken}} calls.', {
              nonPatientToken,
            })}
          />
        );
      }

      if (isSmallMode) {
        const schedulingOutcomeLabel = dataLabels.schedulingOutcomeV2?.[schedulingOutcomeV2] || schedulingOutcomeV2;
        return (
          <SchedulingChip
            call={callInsights as CallIntelTypes.CallRecord}
            isSmallMode
            schedulingOutcome={schedulingOutcomeV2}
            trackingContext='call-history-table'
            tooltipLabel={
              shouldShowReason
                ? getReasonTooltipLabel({
                    label: schedulingOutcomeLabel,
                    reason: reasonText,
                  })
                : undefined
            }
          />
        );
      }

      return (
        <CallIntelCellContent
          showReason={shouldShowReason}
          reason={reasonText}
          isEdited={isFieldEdited?.isSchedulingOutcomeEdited}
          editedText={t('Edited')}
        >
          <SchedulingChip
            call={callInsights as CallIntelTypes.CallRecord}
            schedulingOutcome={schedulingOutcomeV2}
            trackingContext='call-history-table'
          />
        </CallIntelCellContent>
      );
    }

    return (
      <CallIntelCellContent isEdited={isFieldEdited?.isSchedulingOutcomeEdited} editedText={t('Edited')}>
        <EmptyCell tooltipText={t('No Scheduling Outcome due to no Opportunity.')}>{t('N/A')}</EmptyCell>
      </CallIntelCellContent>
    );
  },
  HeaderLabel: () => i18next.t('Scheduling Outcome', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => {
      if (!callInsights.schedulingOutcomeV2) return false;

      return ![
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NOT_APPLICABLE,
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN,
      ].includes(callInsights.schedulingOutcomeV2);
    },
  }),
};

export const SentimentColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');

    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Customer Sentiment')}
        infoTip={<CallIntelTooltip noCta tip='sentiments' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isSmallMode,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
    showReason = false,
  }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { nonPatient: nonPatientToken } = useTranslationTokens();
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
    });
    if (renderer) return isSmallMode ? null : renderer;

    const { callSentiment, isFieldEdited } = callInsights;

    const sentiment = callSentiment?.sentiment;

    const sentimentLabel = sentiment ? dataLabels.sentiments?.[sentiment] || sentiment : '';

    const reasonText = getReasonText(callInsights.callSentimentDetails?.reasons);

    const shouldShowReason =
      showReason && sentiment === CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE && !!reasonText;

    if (isSmallMode && sentiment) {
      return (
        <CallIntelChip
          as='chip'
          chipVariants={chipVariants}
          dataKey={sentiment}
          tooltipLabel={
            shouldShowReason
              ? getReasonTooltipLabel({
                  label: sentimentLabel,
                  reason: reasonText,
                })
              : sentimentLabel
          }
          key={callSentiment.sentiment}
        />
      );
    }

    if (sentiment) {
      return (
        <CallIntelCellContent
          showReason={shouldShowReason}
          reason={reasonText}
          isEdited={isFieldEdited?.isSentimentEdited}
          editedText={t('Edited')}
        >
          <CallIntelChip
            as='chip'
            chipVariants={chipVariants}
            dataKey={sentiment}
            label={sentimentLabel}
            key={sentiment}
          />
        </CallIntelCellContent>
      );
    }

    if (isSmallMode) return null;

    // Sentiment can only be null in case of non-patient calls,so not checking for contact type
    return (
      <EmptyCell
        edited={isFieldEdited?.isSentimentEdited}
        tooltipText={t('Customer Sentiment isn’t analyzed for {{nonPatientToken}} calls.', {
          nonPatientToken,
        })}
      />
    );
  },
  HeaderLabel: () => i18next.t('Customer Sentiment', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => !!callInsights.callSentiment?.sentiment,
  }),
};

export const CategoryColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Category')}
        infoTip={<CallIntelTooltip noCta tip='callsByCategory' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isSmallMode,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
  }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
    });
    if (renderer) return isSmallMode ? null : renderer;

    const { categories, isFieldEdited } = callInsights;

    if (isSmallMode && categories?.length) {
      return categories.map((category: string) =>
        category.trim() ? (
          <CallIntelChip
            as='category'
            chipVariants={chipVariants}
            dataKey={category}
            label={dataLabels.categories?.[category] || category}
            key={category}
          />
        ) : null
      );
    }

    if (isSmallMode) return null;

    if (!categories) return <EmptyCell showTooltip={false} />;

    return categories?.length ? (
      <CallIntelCellContent
        isEdited={isFieldEdited?.isCategoryEdited}
        editedText={t('Edited')}
        customStyles={styles.chips}
      >
        {categories.map((category: string) =>
          category.trim() ? (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={category}
              label={dataLabels.categories?.[category] || category}
              key={category}
            />
          ) : null
        )}
      </CallIntelCellContent>
    ) : (
      <CallIntelCellContent isEdited={isFieldEdited?.isCategoryEdited}>
        <EmptyCell showTooltip={false} />
      </CallIntelCellContent>
    );
  },
  HeaderLabel: () => i18next.t('Category', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => {
      return !!(callInsights.categories && callInsights.categories.length > 0);
    },
  }),
};

export const AppointmentTypeColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Appointment Type')}
        infoTip={<CallIntelTooltip noCta tip='callsByAppointmentType' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isSmallMode,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
  }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { nonPatient: nonPatientToken } = useTranslationTokens();
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
    });
    if (renderer) return isSmallMode ? null : renderer;

    const { appointmentTypes, contactType, isFieldEdited } = callInsights;

    if (isSmallMode && appointmentTypes?.length) {
      return appointmentTypes.map((appointmentType: string) =>
        appointmentType.trim() ? (
          <CallIntelChip
            as='category'
            chipVariants={chipVariants}
            dataKey={appointmentType}
            label={dataLabels.appointmentTypes?.[appointmentType] || appointmentType}
            key={appointmentType}
          />
        ) : null
      );
    }

    return appointmentTypes?.length ? (
      <CallIntelCellContent
        isEdited={isFieldEdited?.isAppointmentTypeEdited}
        editedText={t('Edited')}
        customStyles={styles.chips}
      >
        {appointmentTypes.map((appointmentType: string) =>
          appointmentType.trim() ? (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={appointmentType}
              label={dataLabels.appointmentTypes?.[appointmentType] || appointmentType}
              key={appointmentType}
            />
          ) : null
        )}
      </CallIntelCellContent>
    ) : (
      <CallIntelCellContent isEdited={isFieldEdited?.isAppointmentTypeEdited}>
        <EmptyCell
          tooltipText={
            contactType !== CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT
              ? t('No Appointment Type discussed on the call.')
              : t('Appointment Type isn’t analyzed for {{nonPatientToken}} calls.', {
                  nonPatientToken,
                })
          }
        />
      </CallIntelCellContent>
    );
  },
  HeaderLabel: () => i18next.t('Appointment Type', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => {
      return !!(callInsights.appointmentTypes && callInsights.appointmentTypes.length > 0);
    },
  }),
};

export const TaskColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Tasks')}
        infoTip={<CallIntelTooltip noCta tip='tasks' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    taskCount,
    withTaskChip = true,
    trackingId,
    isAiTaskCreationEnabled,
    isSmallMode,
    onClick,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
  }: ColumnCellProps & {
    taskCount?: number;
    onClick?: () => void;
    withTaskChip?: boolean;
    trackingId?: string;
    isAiTaskCreationEnabled?: boolean;
  }) => {
    const { t } = useTranslation('call-intel-core');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
      overrides: { outboundNoInsightsTooltip: t('No Tasks') },
    });

    if (renderer) return isSmallMode ? null : renderer;

    if (isAiTaskCreationEnabled === false) {
      if (isSmallMode) return null;
      return <EmptyCell tooltipText={t('Tasks are disabled for this location.')} />;
    }

    if (isSmallMode) {
      return taskCount ? <TaskChip taskCount={taskCount} onClick={onClick} /> : null;
    }

    return (
      <>
        {taskCount ? (
          withTaskChip ? (
            <TaskChip taskCount={taskCount} onClick={onClick} />
          ) : (
            <TextLink onClick={onClick} trackingId={trackingId}>
              {t(`{{ taskCount }} ${taskCount == 1 ? 'Task' : 'Tasks'}`, { taskCount })}
            </TextLink>
          )
        ) : (
          <EmptyCell tooltipText={t('No Tasks')} />
        )}
      </>
    );
  },
  HeaderLabel: () => i18next.t('Tasks', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => {
      const taskCount = callInsights?.taskCount;
      return !!taskCount && taskCount > 0;
    },
    checkFeatureEnabled: (props) => {
      const isAiTaskCreationEnabled = (props as { isAiTaskCreationEnabled?: boolean }).isAiTaskCreationEnabled;
      return isAiTaskCreationEnabled !== false;
    },
  }),
};

export const TaskTypeColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Follow-up Reason')}
        infoTip={<CallIntelTooltip noCta tip='followUpReason' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isAiTaskCreationEnabled,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
  }: ColumnCellProps & { isAiTaskCreationEnabled?: boolean }) => {
    const { t } = useTranslation('call-intel-core');
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
    });
    if (renderer) return renderer;

    const { taskTypes } = callInsights;

    if (isAiTaskCreationEnabled === false) {
      return <EmptyCell tooltipText={t('Tasks are disabled for this location.')} />;
    }

    return taskTypes?.length ? (
      <div css={styles.chips}>
        {taskTypes?.map((taskType: string) =>
          taskType.trim() ? (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={taskType}
              label={dataLabels.taskTypes?.[taskType] || taskType}
              key={taskType}
            />
          ) : null
        )}
      </div>
    ) : (
      <EmptyCell tooltipText={t('No Tasks')} />
    );
  },
  HeaderLabel: () => i18next.t('Follow-up Reason', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => {
      return !!(callInsights.taskTypes && callInsights.taskTypes.length > 0);
    },
    checkFeatureEnabled: (props) => {
      const isAiTaskCreationEnabled = (props as { isAiTaskCreationEnabled?: boolean }).isAiTaskCreationEnabled;
      return isAiTaskCreationEnabled !== false;
    },
  }),
};

export const ServiceIssueColumn = {
  Header: ({ showCallIntelPrefix, showAiIcon = true }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        badge={<AnimatedBadge label={t('Beta')} animated={false} bgColor={theme.colors.indigo50} />}
        columnTitle={t('Service Issues')}
        infoTip={<CallIntelTooltip noCta tip='serviceIssues' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon={showAiIcon}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    isSmallMode,
    overrideServiceIssues,
    onClick,
    isCallIntelAccessRestricted,
    callIntelAccessRestrictedTooltip,
  }: ColumnCellProps & { overrideServiceIssues?: ServiceQualityFlag[]; onClick?: () => void }) => {
    const { t } = useTranslation('call-intel-core');
    const { nonPatient: nonPatientToken } = useTranslationTokens();
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      isCallIntelAccessRestricted,
      callIntelAccessRestrictedTooltip,
    });
    if (renderer) return isSmallMode ? null : renderer;

    const { callSentiment, contactType, serviceQualityFlags, isFieldEdited } = callInsights;

    const sentiment = callSentiment?.sentiment as CallIntelTypes.SentimentEnum;

    const nonAnalyzedSentiments = [
      CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
      CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
    ];

    if (isSmallMode && !serviceQualityFlags?.length) return null;

    if (isSmallMode && serviceQualityFlags?.length) {
      return (
        <ChipGroup<CallIntelTypes.ServiceQualityFlagEnum>
          items={sortWithPriorityMap<CallIntelTypes.ServiceQualityFlagEnum>(
            serviceQualityFlags,
            ServiceQualityFlagPriorityMap,
            overrideServiceIssues
          )}
          renderChip={(flag) => (
            <CallIntelChip
              as='serviceQualityFlag'
              chipVariants={chipVariants}
              dataKey={flag}
              tooltipLabel={dataLabels.serviceQualityFlags?.[flag] || flag}
              key={flag}
            />
          )}
          maxVisible={serviceQualityFlags?.length}
        />
      );
    }

    return serviceQualityFlags?.length ? (
      <CallIntelCellContent
        isEdited={isFieldEdited?.isServiceQualityFlagEdited}
        editedText={t('Edited')}
        customStyles={styles.chips}
      >
        <ChipGroup<CallIntelTypes.ServiceQualityFlagEnum>
          items={sortWithPriorityMap<CallIntelTypes.ServiceQualityFlagEnum>(
            serviceQualityFlags,
            ServiceQualityFlagPriorityMap,
            overrideServiceIssues
          )}
          renderChip={(flag) => (
            <CallIntelChip
              as='serviceQualityFlag'
              chipVariants={chipVariants}
              dataKey={flag}
              label={dataLabels.serviceQualityFlags?.[flag] || flag}
              key={flag}
            />
          )}
          onClick={onClick}
        />
      </CallIntelCellContent>
    ) : (
      <CallIntelCellContent isEdited={isFieldEdited?.isServiceQualityFlagEdited}>
        <EmptyCell
          tooltipText={
            contactType === CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT
              ? t('Service Issues aren’t analyzed for {{nonPatientToken}} calls.', {
                  nonPatientToken,
                })
              : nonAnalyzedSentiments.includes(sentiment)
              ? t('Service Issues aren’t analyzed for happy or neutral calls.')
              : t('No Service Issues')
          }
        />
      </CallIntelCellContent>
    );
  },
  HeaderLabel: () => i18next.t('Service Issues', { ns: 'call-intel-core' }),
  shouldRender: createShouldRender({
    checkContent: (callInsights) => !!(callInsights.serviceQualityFlags && callInsights.serviceQualityFlags.length > 0),
  }),
};

const styles = {
  chips: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    align-items: center;
  `,

  editedText: css`
    font-style: italic;
  `,

  fieldWrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    align-items: center;
    width: 100%;
  `,

  reasonText: css`
    width: 100%;
    color: var(--fabric-color-text-secondary);
  `,
};
